import { OrderRegisterStatusType, OrderRegisterType, OrderStatusType, OrderFlagType, FiscalPaymentType, FiscalTransactionExtensionType, PaymentTypeLabel } from '../../types';
import { Order, OrderItem, TaxRate, TaxItem, Shift, CashRegister } from '../../models';
import { LatToCyr, Money, dateFormatter } from '../../utilities';
import { I18n, Settings, Store } from '../../wrappers';
import { fiscalService } from '../../fiscal';
import { EinvoiceService } from '../../services';
import { Printable, receiptLogoFor } from '../../print';
import moment from 'moment-timezone';
export class OrderLocalizationSr {
    order;
    constructor(order) {
        this.order = order;
    }
    static fiscalStatuses() {
        return [OrderStatusType.paid, OrderStatusType.advance, OrderStatusType.proforma, OrderStatusType.copy];
    }
    /**
     * The receipt's own venue when the caller has it (SAP renders a client's receipt without a
     * logged-in tenant), else the logged-in tenant's; neither → no QR code.
     */
    getPaymentQRCode(venue) {
        const resolvedVenue = venue ?? Store.get('tenant')?.venue;
        const venueBankNumber = resolvedVenue?.bank_account_number?.replace(/-/g, '');
        const venueName = resolvedVenue?.name;
        if (!venueBankNumber || !venueName)
            return null;
        const paidInvoice = this.order.getPaidInvoice();
        if (paidInvoice.toUnit() <= 0)
            return null;
        // NBS QR code format: K=Type, V=Version, C=Character set, R=Account, N=Name, I=Amount, SF=Purpose code, S=Description
        const qrCode = `K:PR|V:01|C:1|R:${venueBankNumber}|N:${venueName}|I:RSD${paidInvoice.toFormat('0.00').replace('.', ',')}|SF:221|S:Konty Račun-Instant`;
        return qrCode;
    }
    isImportedFromOldFiscalSystem() {
        const refFlag = this.order.getFlag(OrderFlagType.ref);
        return refFlag.length > 0 && refFlag[0].toLowerCase().startsWith('xxxxxxxx');
    }
    shouldHaveNumber() {
        // External documents carry no receipt number anywhere — the server
        // nulls it on create, so numbering one locally would diverge.
        if (this.order.isExternal())
            return false;
        if (EinvoiceService.einvoiceOnly() && this.order.status == OrderStatusType.proforma) {
            return false;
        }
        return this.order.status != OrderStatusType.unpaid;
    }
    /**
     * An e-invoice-only venue never fiscalizes a proforma (no register type,
     * no number, nothing from the fiscal device): it is the same plain
     * commercial document as a manually registered invoice. Orders that
     * predate the register type carry none either, so the fiscal-device
     * response is what tells a fiscalized one apart.
     */
    rendersManualInvoice() {
        return this.order.register_type === OrderRegisterType.manual
            || (this.order.isProforma() && !this.order.register_type && !this.order.register_response);
    }
    shouldPrintReceipt() {
        return !this.order.isUnpaid();
    }
    shouldRegister() {
        return (!this.order.register_status || this.order.register_status === OrderRegisterStatusType.failed)
            && OrderLocalizationSr.fiscalStatuses().includes(this.order.status)
            && this.order.isFiscal()
            && !EinvoiceService.einvoiceOnly();
    }
    getRegisterRequest() {
        const isRefund = !!this.order.getFlag(OrderFlagType.correctType)[0] || this.order.getToPay().toUnit() < 0;
        const transactionType = !isRefund ? 'Sale' : 'Refund';
        let invoiceType = 'Normal';
        if (this.order.isProforma())
            invoiceType = 'ProForma';
        else if (this.order.isCopy())
            invoiceType = 'Copy';
        else if (this.order.isTraining())
            invoiceType = 'Training';
        else if (this.order.isAdvance())
            invoiceType = 'Advance';
        const user = this.order.getUser();
        const client = this.order.getClient();
        const payments = [];
        const addPayment = (amount, paymentType) => {
            const existingIndex = payments.findIndex(p => p.paymentType === paymentType);
            if (existingIndex !== -1)
                payments[existingIndex].amount += amount;
            else
                payments.push({ amount, paymentType });
        };
        let paidCash = this.order.getPaidCash();
        let paidCreditCard = this.order.getPaidCreditCard();
        const curCashRegister = CashRegister.current();
        if (curCashRegister && !curCashRegister.allowsPayment(PaymentTypeLabel.credit_card) && I18n.c('cashRegister.allowLimitedPayments')) {
            paidCash = paidCash.add(paidCreditCard);
            paidCreditCard = Money.parse(0);
        }
        if (!paidCash.isZero())
            addPayment(paidCash.add(this.order.getChange()).abs().toUnit(), FiscalPaymentType.Cash);
        if (!paidCreditCard.isZero())
            addPayment(paidCreditCard.abs().toUnit(), FiscalPaymentType.Card);
        if (!this.order.getPaidInvoice().isZero())
            addPayment(this.order.getPaidInvoice().abs().toUnit(), FiscalPaymentType.WireTransfer);
        if (!this.order.getPaidCheck().isZero())
            addPayment(this.order.getPaidCheck().abs().toUnit(), FiscalPaymentType.Check);
        if (!this.order.getPaidVoucher().isZero())
            addPayment(this.order.getPaidVoucher().abs().toUnit(), FiscalPaymentType.Voucher);
        if (!this.order.getPaidMobileMoney().isZero())
            addPayment(this.order.getPaidMobileMoney().abs().toUnit(), FiscalPaymentType.MobileMoney);
        if (!this.order.getPaidOtherNoncash().isZero())
            addPayment(this.order.getPaidOtherNoncash().abs().toUnit(), FiscalPaymentType.Other);
        // in case of final receipt fully paid from advance set the payment to be the payment of the first advance
        if (this.order.getPaidAdvance().toUnit() != 0 && this.order.getLeftToPay().toRoundedUnit(2) == 0) {
            if (isRefund) {
                const payments = this.order.correctsOrders()[0]?.register_request?.payment || [];
                for (const p of payments) {
                    addPayment(0, p.paymentType);
                }
            }
            else {
                const advanceRefOrders = this.order.referredAdvanceOrders();
                const payment = advanceRefOrders[0]?.getPayments().find(p => p.key !== PaymentTypeLabel.advance);
                let paymentType = FiscalPaymentType.Cash;
                if (payment?.key === PaymentTypeLabel.credit_card)
                    paymentType = FiscalPaymentType.Card;
                if (payment?.key === PaymentTypeLabel.invoice)
                    paymentType = FiscalPaymentType.WireTransfer;
                if (payment?.key === PaymentTypeLabel.check)
                    paymentType = FiscalPaymentType.Check;
                if (payment?.key === PaymentTypeLabel.voucher)
                    paymentType = FiscalPaymentType.Voucher;
                if (payment?.key === PaymentTypeLabel.mobile_money)
                    paymentType = FiscalPaymentType.MobileMoney;
                if (payment?.key === PaymentTypeLabel.other_noncash)
                    paymentType = FiscalPaymentType.Other;
                addPayment(0, paymentType);
            }
        }
        const items = this.order.isSummary()
            ? this.order.paysForOrders()
                ? this.order.paysForOrders().flatMap(order => order.items)
                : []
            : this.order.items;
        const invoice = {
            requestId: this.order.hash,
            invoiceType: invoiceType,
            transactionType: transactionType,
            payment: payments,
            invoiceNumber: I18n.c('general.certifiedAppId') + '/' + Settings.certifiedVersion,
            cashier: user?.name || '',
            options: {
                OmitTextualRepresentation: 0,
                OmitQRCodeGen: 1
            },
            items: items.map(i => {
                const taxRate = TaxRate.find(i.tax_rate_id);
                const unit = i.unit_of_measure || 'pcs';
                const namePrefix = this.order.getFlag(OrderFlagType.type).includes('noCompensation') ? '00: ' : '';
                const item = {
                    code: i.getProduct()?.code || undefined,
                    name: namePrefix + i.name,
                    quantity: Math.abs(i.getCount()),
                    discount: i.getDiscountPercent(),
                    labels: taxRate?.label ? [taxRate.label] : [],
                    unitPrice: i.getUnitPriceAfterDiscount().abs().toRoundedUnit(2),
                    totalAmount: i.getToPay().abs().toRoundedUnit(2),
                    unit: I18n.t(`products.unit_of_measure.${unit}`) !== `products.unit_of_measure.${unit}`
                        ? I18n.t(`products.unit_of_measure.${unit}`)
                        : unit
                };
                if (i.gtin) {
                    item.gtin = i.gtin;
                }
                if (i.comments.length) {
                    item.note = i.comments.join('\n');
                }
                return item;
            })
        };
        const formatToIsoString = (date) => {
            if (!date.isValid())
                return undefined;
            return date.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
        };
        const advancePaidDate = this.order.due_date;
        if (invoice.invoiceType === 'Advance' && invoice.transactionType === 'Sale' && advancePaidDate) {
            const date = formatToIsoString(moment(advancePaidDate, 'YYYY-MM-DD HH:mm:ss'));
            if (date) {
                invoice.dateAndTimeOfIssue = date;
            }
        }
        // A linked refund's link to its advance never decides the reference: "Ref. broj" (the flag) does.
        const refOrder = this.order.refers_to.find(order => order.reference?.type !== 'linkedRefund') ?? null;
        if (refOrder) {
            if (refOrder.register_number && refOrder.registered_at) {
                invoice.referentDocumentNumber = refOrder.register_number;
                invoice.referentDocumentDT = formatToIsoString(moment.utc(refOrder.registered_at).tz('Europe/Belgrade'));
            }
        }
        else if (this.order.flagsInclude(OrderFlagType.ref)) {
            const reference = this.order.getFlag(OrderFlagType.ref);
            invoice.referentDocumentNumber = reference[0];
            if (reference[1]) {
                invoice.referentDocumentDT = formatToIsoString(moment.tz(reference[1], 'YYYY-MM-DD HH:mm:ss', 'Europe/Belgrade'));
            }
        }
        if (transactionType === 'Refund' && this.order.getFlag(OrderFlagType.reason)[0] === 'error') {
            const tenant = Store.get('tenant');
            invoice.buyerId = `10:${tenant?.venue?.tax_id_number}`;
        }
        else if (client?.id_number) {
            invoice.buyerId = client.getFullIdNumber();
            if (client.cost_center && ['Normal', 'Advance'].includes(invoice.invoiceType) && invoice.transactionType === 'Sale') {
                invoice.buyerCostCenterId = client.cost_center;
            }
        }
        return invoice;
    }
    async register(perfTracker) {
        if (!this.shouldRegister()) {
            return { success: false, error: I18n.t('general.action_failed') };
        }
        console.log('Order register ' + this.order.hash);
        perfTracker?.startStep('build_fiscal_request');
        const request = this.getRegisterRequest();
        perfTracker?.endStep(); // build_fiscal_request
        perfTracker?.startStep('fiscal_register_call');
        const response = await fiscalService.createInvoice(request);
        perfTracker?.endStep(); // fiscal_register_call
        perfTracker?.startStep('handle_fiscal_response');
        this.order.register_request = request;
        const result = this.handleRegisterResponse(request, response);
        perfTracker?.endStep(); // handle_fiscal_response
        return result;
    }
    async handleRegisterResponse(request, response) {
        if (!response.success) {
            // Report the register error
            console.log('Order register error', response);
            const error = response.error?.message;
            Order.errorReporter.reportMessage('Order register error', {
                request: JSON.stringify(request),
                response: JSON.stringify(response),
                orderHash: this.order.hash,
                error,
            });
            // If request to SDC timed out, give it some more and try to fetch the registered receipt from it
            const isTimeoutError = response.error?.code === 'timeout';
            const isDuplicateInvoiceError = response.error?.code === 'duplicated_invoice_request';
            if (!isTimeoutError && !isDuplicateInvoiceError)
                return { success: false, error };
            if (isTimeoutError) {
                console.log('Order register timeout. Wait for retry.');
                const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
                await delay(15000);
            }
            if (isDuplicateInvoiceError) {
                console.log('Order already exists in SDC');
            }
            const existingOrder = Order.find(this.order.hash);
            // Receipt exists in SDC and is saved in store
            if (existingOrder?.register_status === OrderRegisterStatusType.success) {
                console.log('Order found in local store');
                Order.errorReporter.reportMessage('Order register error: already_exists_pos', { orderHash: this.order.hash });
                this.order.$fill(existingOrder.$toJson());
                return { success: true };
            }
            // Receipt exists in SDC and but is NOT saved in store
            Order.errorReporter.reportMessage('Order register error: already_exists_sdc', { orderHash: this.order.hash });
            console.log('Order not found in local store. Try fetching it from SDC.');
            const existingRegisterResponse = await fiscalService.getInvoice(this.order.hash);
            if (!existingRegisterResponse.success) {
                console.log('Failed to retrieve order from SDC');
                return { success: false, error };
            }
            ;
            console.log('Order successfully retrieved from SDC');
            response = existingRegisterResponse;
        }
        this.order.register_status = OrderRegisterStatusType.success;
        this.order.register_response = response.data;
        if (this.order.refersToAdvance()) {
            const advanceRefOrder = this.order.referredAdvanceOrders()[0];
            this.order.register_response.advancePaid = this.order.getPaidAdvance().toRoundedUnit(4);
            this.order.register_response.advanceTax = advanceRefOrder.getTax().abs().toRoundedUnit(4);
        }
        const copiesOrder = this.order.copiesOrder();
        if (copiesOrder?.getPaidAdvance().toUnit()) {
            this.order.register_response.advancePaid = copiesOrder.register_response?.advancePaid;
            this.order.register_response.advanceTax = copiesOrder.register_response?.advanceTax;
        }
        const correctsOrder = this.order.correctsOrders()[0];
        if (correctsOrder?.getPaidAdvance().toUnit()) {
            this.order.register_response.advancePaid = correctsOrder.register_response?.advancePaid;
            this.order.register_response.advanceTax = correctsOrder.register_response?.advanceTax;
        }
        this.order.registered_at = moment(response.data.sdcDateTime).utc().format(Order.DATE_FORMAT);
        this.order.register_number = response.data.invoiceNumber;
        delete this.order.register_response.verificationQRCode;
        delete this.order.register_response.encryptedInternalData;
        delete this.order.register_response.signature;
        delete this.order.register_response.journal;
        return { success: true };
    }
    getTaxes() {
        if (!this.order.isCreditNote() && this.order.isFiscal()) {
            const invoice = (this.order.register_request || this.getRegisterRequest());
            const taxesCalculation = fiscalService.calculateTaxes(invoice);
            const taxItems = taxesCalculation ? taxesCalculation.taxItems : [];
            return taxItems.map(taxItem => TaxItem.createForSrFiscalTaxItem(this.order, taxItem, invoice, taxesCalculation?.taxGroupRevision || 0))
                .filter((taxItem) => !!taxItem);
        }
        return [
            ...this.order.getTaxesOnItems(),
            ...this.order.getTaxesOnTotal()
        ];
    }
    getTax() {
        if (this.order.register_response && this.order.isFiscal()) {
            return this.order.register_response.taxItems.reduce((acc, cur) => acc.add(Money.parse(cur.amount)), Money.parse(0));
        }
        return this.order.taxes.reduce((acc, cur) => {
            const taxRateAmount = Money.parse(cur.value);
            return acc.add(Money.parse(taxRateAmount.toRoundedUnit(2)));
        }, Money.parse(0));
    }
    async generateQRCodeString() {
        return this.order.register_response?.verificationUrl || '';
    }
    getPriceBeforeTax() {
        if (this.order.register_response && this.order.isFiscal()) {
            const totalTax = this.order.register_response.taxItems.reduce((acc, cur) => acc.add(Money.parse(Money.parse(cur.amount).toRoundedUnit(2))), Money.parse(0));
            return Money.parse(this.order.register_response.totalAmount).subtract(totalTax);
        }
        return this.order.getToPay().abs().subtract(this.order.getTax());
    }
    getPaid() {
        if (!this.order.isFiscal()) {
            return this.order.getPaidCash()
                .add(this.order.getPaidCreditCard())
                .add(this.order.getPaidInvoice())
                .add(this.order.getPaidCreditCardBusiness())
                .add(this.order.getPaidAdvance())
                .add(this.order.getPaidCheck())
                .add(this.order.getPaidVoucher())
                .add(this.order.getPaidMobileMoney())
                .add(this.order.getPaidOtherNoncash())
                .add(this.order.getChange());
        }
        const registerRequest = (this.order.register_request || this.order.getRegisterRequest());
        const totalPaid = registerRequest.payment.reduce((acc, cur) => acc.add(Money.parse(cur.amount)), Money.parse(0));
        const advancePaid = this.order.register_response?.advancePaid
            ? Money.parse(this.order.register_response.advancePaid)
            : this.order.getPaidAdvance().abs();
        return totalPaid.add(advancePaid);
    }
    async getOrderPrintable(options, venue) {
        const { preview } = options;
        const priceFormat = '0,0.00';
        const pr = new Printable();
        let invoice = (this.order.register_request || this.order.getRegisterRequest());
        let sdcResponse = this.order.register_response;
        if (!sdcResponse && preview) {
            sdcResponse = this.getFakeSdcResponse(this.order);
        }
        const tenant = Store.get('tenant');
        const resolvedVenue = venue || tenant?.venue;
        if (!resolvedVenue)
            return pr;
        const receiptNote = resolvedVenue?.receipt_note?.split('|')[0] || '';
        const receiptHeaderText = resolvedVenue?.receipt_note?.split('|')[1] || '';
        const itemCommentsOnFiscalReceipts = !!resolvedVenue?.settings?.item_comments_on_fiscal_receipts;
        if (!invoice || !invoice?.items?.length || !sdcResponse) {
            return pr;
        }
        let totalPaid = invoice.payment.reduce((acc, cur) => acc.add(Money.parse(cur.amount)), Money.parse(0));
        if (sdcResponse.advancePaid) {
            totalPaid = totalPaid.add(Money.parse(sdcResponse.advancePaid));
        }
        const totalAmount = Money.parse(sdcResponse.totalAmount);
        const remaining = invoice.invoiceType === 'Advance'
            ? Money.parse(0)
            : Money.parse(totalAmount.toRoundedUnit(2)).subtract(Money.parse(totalPaid.toRoundedUnit(2)));
        // Above the header text and the fiscal banner: outside the fiscal journal
        const receiptLogo = receiptLogoFor(resolvedVenue);
        if (receiptLogo)
            pr.l('LOGO', receiptLogo);
        if (receiptHeaderText) {
            receiptHeaderText.split('\n').forEach(line => pr.l('L', line, { align: 'C' }));
            pr.l();
        }
        if (['Normal', 'Advance'].includes(invoice.invoiceType)) {
            pr.l('D', LatToCyr.t(I18n.t('receipt.fiscal_receipt')), { delimiter: '==', align: 'C' });
        }
        else {
            pr.l('D', LatToCyr.t(I18n.t('receipt.nonfiscal_receipt')), { delimiter: '==', align: 'C', scale: '1,2' });
            pr.l();
        }
        pr.l('L', sdcResponse.tin, { align: 'C' });
        pr.l('L', sdcResponse.businessName, { align: 'C' });
        pr.l('L', sdcResponse.locationName, { align: 'C' });
        pr.l('L', sdcResponse.address, { align: 'C' });
        pr.l('L', sdcResponse.district, { align: 'C' });
        pr.l('D', '', { delimiter: '--' });
        if (invoice.cashier) {
            pr.l('S', LatToCyr.t(I18n.t('receipt.cashier')), invoice.cashier);
        }
        if (invoice.buyerId) {
            pr.l('S', `${LatToCyr.t(I18n.t('receipt.buyer_id'))}:`, invoice.buyerId);
        }
        if (invoice.buyerCostCenterId) {
            pr.l('S', `${LatToCyr.t(I18n.t('receipt.buyer_cost_center'))}:`, invoice.buyerCostCenterId);
        }
        pr.l('S', `${LatToCyr.t(I18n.t('receipt.number'))}:`, invoice.invoiceNumber);
        if (invoice.invoiceType === 'Advance' && invoice.dateAndTimeOfIssue) {
            pr.l('S', `${LatToCyr.t(I18n.t('receipt.time_pos'))}:`, moment(invoice.dateAndTimeOfIssue).tz('Europe/Belgrade').format('DD.MM.YYYY. HH:mm:ss'));
        }
        if (invoice.referentDocumentNumber) {
            pr.l('S', `${LatToCyr.t(I18n.t('receipt.ref_number'))}:`, invoice.referentDocumentNumber);
        }
        if (invoice.referentDocumentDT) {
            pr.l('S', `${LatToCyr.t(I18n.t('receipt.ref_time'))}:`, moment(invoice.referentDocumentDT).tz('Europe/Belgrade').format('DD.MM.YYYY. HH:mm:ss'));
        }
        pr.l('D', ` ${LatToCyr.t(I18n.t(`receipt.type_${invoice.invoiceType.toLowerCase()}`))} ${LatToCyr.t(I18n.t(`receipt.transaction_${invoice.transactionType.toLowerCase()}`))} `, { delimiter: '--', align: 'C' });
        pr.l('L', LatToCyr.t(I18n.t('receipt.items.heading')));
        pr.l('D', '', { delimiter: '==' });
        pr.table({ colNumber: 4 });
        pr.tl({ content: LatToCyr.t(I18n.t('receipt.items.name')) }, { content: LatToCyr.t(I18n.t('receipt.items.unit_price')), align: 'C' }, { content: LatToCyr.t(I18n.t('receipt.items.quantity')), align: 'C' }, { content: LatToCyr.t(I18n.t('receipt.items.to_pay')), align: 'R' });
        invoice.items.forEach((item) => {
            const labels = item.labels.map((label) => ` (${label})`).join('');
            const unit = item.unit ? '/' + item.unit : '';
            const gtin = item.gtin ? LatToCyr.t('GTIN') + ': ' + item.gtin : '';
            pr.tl({ content: `${item.name}${labels}${unit}`, colSpan: 4 });
            if (itemCommentsOnFiscalReceipts && item.note) {
                item.note.split('\n').forEach((line) => pr.tl({ content: line, colSpan: 4 }));
            }
            if (gtin) {
                pr.tl({ content: gtin, colSpan: 4 });
            }
            const unitPrice = Money.parse(item.unitPrice).toFormat(priceFormat);
            const totalAmount = invoice.transactionType === 'Sale'
                ? Money.parse(item.totalAmount).toFormat(priceFormat)
                : Money.parse(item.totalAmount).multiply(-1).toFormat(priceFormat);
            pr.tl({ content: '' }, { content: unitPrice, align: 'C' }, { content: '' + item.quantity, align: 'C' }, { content: totalAmount, align: 'R' });
        });
        pr.l('D', '', { delimiter: '--' });
        if (invoice.transactionType === 'Sale') {
            pr.l('S', `${LatToCyr.t(I18n.t('receipt.payments.to_pay_sale'))}:`, Money.parse(sdcResponse.totalAmount).toFormat(priceFormat), { style: { fontWeight: '600' } });
        }
        else {
            pr.l('S', `${LatToCyr.t(I18n.t('receipt.payments.to_pay_refund'))}:`, Money.parse(sdcResponse.totalAmount).toFormat(priceFormat), { style: { fontWeight: '600' } });
        }
        if (sdcResponse.advancePaid) {
            pr.l('S', `${LatToCyr.t(I18n.t('receipt.payments.advance'))}:`, Money.parse(sdcResponse.advancePaid).toFormat(priceFormat));
        }
        if (sdcResponse.advanceTax !== undefined) {
            pr.l('S', `${LatToCyr.t(I18n.t('receipt.payments.advance_tax'))}:`, Money.parse(sdcResponse.advanceTax).toFormat(priceFormat));
        }
        invoice.payment.forEach(payment => {
            const paymentType = payment.paymentType === 'MobileMoney' ? 'mobile_money' : payment.paymentType.toLowerCase();
            if (payment.amount != 0) {
                pr.l('S', `${LatToCyr.t(I18n.t(`receipt.payments.${paymentType}`))}:`, Money.parse(payment.amount).toFormat(priceFormat));
            }
        });
        if (remaining.toUnit() < 0) {
            pr.l('S', `${LatToCyr.t(I18n.t('receipt.payments.change'))}:`, remaining.abs().toFormat(priceFormat));
        }
        else if (remaining.toUnit() > 0) {
            pr.l('S', `${LatToCyr.t(I18n.t('receipt.payments.left_to_pay'))}:`, remaining.abs().toFormat(priceFormat));
        }
        pr.l('D', '', { delimiter: '==' });
        if (!['Normal', 'Advance'].includes(invoice.invoiceType)) {
            pr.l('L', LatToCyr.t(I18n.t('receipt.nonfiscal_receipt')), { align: 'C', scale: '1,2', style: { fontWeight: '600' } });
            pr.l('D', '', { delimiter: '==' });
        }
        pr.table({ colNumber: 4 });
        pr.tl({ content: LatToCyr.t(I18n.t('receipt.taxes.label')) }, { content: LatToCyr.t(I18n.t('receipt.taxes.name')), align: 'C' }, { content: LatToCyr.t(I18n.t('receipt.taxes.rate')), align: 'C' }, { content: LatToCyr.t(I18n.t('receipt.taxes.amount')), align: 'R' });
        sdcResponse.taxItems.forEach(taxItem => {
            pr.tl({ content: taxItem.label }, { content: taxItem.categoryName, align: 'C' }, { content: `${Money.parse(taxItem.rate).toFormat(priceFormat)}%`, align: 'C' }, { content: Money.parse(taxItem.amount).toFormat(priceFormat), align: 'R' });
        });
        pr.l('D', '', { delimiter: '--' });
        const totalTax = sdcResponse.taxItems.reduce((acc, cur) => {
            const taxRateAmount = Money.parse(cur.amount);
            return acc.add(Money.parse(taxRateAmount.toRoundedUnit(2)));
        }, Money.parse(0));
        pr.l('S', `${LatToCyr.t(I18n.t('receipt.taxes.total'))}:`, totalTax.toFormat(priceFormat));
        pr.l('D', '', { delimiter: '==' });
        pr.l('S', `${LatToCyr.t(I18n.t('receipt.sdc.time'))}:`, moment(sdcResponse.sdcDateTime).tz('Europe/Belgrade').format('DD.MM.YYYY. HH:mm:ss'));
        pr.l('S', `${LatToCyr.t(I18n.t('receipt.sdc.number'))}:`, sdcResponse.invoiceNumber);
        pr.l('S', `${LatToCyr.t(I18n.t('receipt.sdc.counter'))}:`, sdcResponse.invoiceCounter);
        pr.l('D', '', { delimiter: '==' });
        pr.l('QR', sdcResponse.verificationUrl, { size: '53mm' });
        if (invoice.invoiceType === 'Copy' && invoice.transactionType === 'Refund') {
            pr.l();
            pr.l('D', LatToCyr.t(I18n.t('receipt.buyer_signature')) + ': ', { delimiter: '_' });
            pr.l();
        }
        if (['Normal', 'Advance'].includes(invoice.invoiceType)) {
            pr.l('D', LatToCyr.t(I18n.t('receipt.fiscal_receipt_end')), { align: 'C', delimiter: '==' });
        }
        else {
            pr.l('D', LatToCyr.t(I18n.t('receipt.nonfiscal_receipt_end')), { delimiter: '==', align: 'C', scale: '1,2' });
        }
        pr.l();
        if (this.order.getTable()) {
            pr.l('L', `${I18n.t('receipt.table')}: ${this.order.getTable()?.name || ''}`);
            pr.l('D', '', { delimiter: '--' });
        }
        // Client
        let client = this.order.getClient();
        if (client && !this.order.isIncoming()) {
            pr.l('L', `${LatToCyr.t(I18n.t('general.buyer'))}: ${client.name}`);
            if (client.address) {
                pr.l('L', `${client.address}${client.city ? (', ' + client.city) : ''}${client.country ? (', ' + client.country) : ''}`);
            }
            if (client.registration_number) {
                pr.l('L', `${LatToCyr.t(I18n.t('general.registration_number'))}: ${client.registration_number}`);
            }
            if (client?.comment) {
                client.comment.split('\n').forEach((line) => pr.l('L', line));
            }
            pr.l('D', '', { delimiter: '--' });
        }
        if (this.order.note) {
            this.order.note.split('\n').forEach((line) => pr.l('L', line));
            pr.l('D', '', { delimiter: '--' });
        }
        if (receiptNote) {
            receiptNote.split('\n').forEach(line => pr.l('L', line));
            pr.l('D', '', { delimiter: '--' });
        }
        if (this.order.refersToAdvance()) {
            const advanceRefund = this.order.refers_to[0];
            if (advanceRefund) {
                const advanceRefundInvoice = (advanceRefund.register_request || advanceRefund.getRegisterRequest());
                pr.l('L', `${LatToCyr.t(I18n.t('receipt.last_advance_receipt'))}:`);
                pr.l('L', `${advanceRefundInvoice.referentDocumentNumber} ${moment(advanceRefundInvoice.referentDocumentDT).tz('Europe/Belgrade').format('DD.MM.YYYY.')}`);
                pr.l('D', '', { delimiter: '--' });
            }
        }
        if (this.order.getDiscountAmount().toUnit() > 0) {
            pr.l('S', `${LatToCyr.t(I18n.t('receipt.discount_note'))}:`, this.order.getDiscountAmount().toFormat());
            pr.l('D', '', { delimiter: '--' });
        }
        if (this.order.getFlag(OrderFlagType.type).includes('noCompensation')) {
            pr.l('L', `* ${LatToCyr.t(I18n.t('general.payment_without_compensation'))}`);
            pr.l('D', '', { delimiter: '--' });
        }
        const domain = I18n.c('general.domain');
        if (domain) {
            pr.l('L', `www.${domain}`, { align: 'C' });
        }
        return pr;
    }
    async getUnpaidOrderPrintable(options, venue) {
        const pr = new Printable();
        const { formatDateTimeHuman } = dateFormatter();
        const { printingSector } = options;
        const curShift = Shift.current();
        const curShiftOrders = curShift ? curShift.getOrders() : [];
        const orderNumber = curShiftOrders.length + 1;
        // Prefer the stable callout code stored at fire time; fall back to the
        // live shift counter for historical orders issued before callout codes.
        const calloutCode = this.order.getFlag(OrderFlagType.calloutCode)?.[0];
        const displayCode = calloutCode || String(orderNumber);
        pr.l();
        const isRefunded = this.order.canceled() || this.order.getToPay().toUnit() < 0;
        if (isRefunded) {
            pr.l('L', I18n.t('general.order_storno').toUpperCase(), { align: 'C', scale: '3,3' });
            pr.l();
        }
        if (printingSector) {
            pr.l('L', `${displayCode} # ${I18n.t('products.printing_sector.' + printingSector)}`, { align: 'C', scale: '2,2' });
            pr.l();
        }
        pr.l('D', '', { delimiter: '-' });
        if (this.order.isDelivery()) {
            // The banner above carries the callout; this line only names the
            // platform so the kitchen knows the order leaves the venue.
            const platformLabel = this.order.source.charAt(0).toUpperCase() + this.order.source.slice(1);
            pr.l('L', platformLabel, { scale: '2,2' });
        }
        else {
            const table = this.order.getTable();
            if (table) {
                const sector = this.order.getSector();
                const tableLabel = table.name + (sector ? ` (${sector.name})` : '');
                const guestId = this.order.getGuestId();
                const guestLabel = guestId !== null ? ` / G${guestId + 1}` : '';
                // A guest-placed round: the kitchen sees the table like any
                // waiter round, marked so staff know nobody keyed it in.
                const sourceLabel = this.order.isQrOrder() ? ' / QR' : '';
                pr.l('L', `${I18n.t('general.table')}: ${tableLabel}${guestLabel}${sourceLabel}`);
            }
        }
        const lastActivityAt = this.order.getLastActivityAt();
        pr.l('L', `${I18n.t('general.time')}: ${lastActivityAt ? formatDateTimeHuman(lastActivityAt) : ''}`);
        pr.l('B', `${I18n.t('general.issued_by')}:`, this.order.getUser()?.name || '');
        pr.l('D', '', { delimiter: '-' });
        // Product Items
        let productItems = this.order.items.map((item, index) => {
            item.index = index;
            return item;
        });
        // Item separators
        const separators = this.order.getItemSeparators();
        if (printingSector && printingSector !== 'order') {
            productItems = productItems.filter(item => {
                const product = item.getProduct();
                return !product || product.printing_sector === printingSector;
            });
        }
        pr.l();
        productItems.forEach((item, index) => {
            if (index !== 0 && separators.length && separators[0] <= item.index) {
                pr.l('D', '', { delimiter: '-' });
                pr.l();
                separators.shift();
            }
            const count = isRefunded ? -1 * Math.abs(item.getCount()) : item.getCount();
            pr.l('L', `${count} x ${item.name}`, { scale: '2,2' });
            if (item.comments.length) {
                pr.l();
            }
            item.comments.forEach((comment) => {
                pr.l('L', `- ${comment}`);
            });
            pr.l();
        });
        return pr;
    }
    getExemptFromTaxReasons(orderItem) {
        let reasons = OrderItem.exemptFromTaxReasons();
        const otherItemsWithExemptFromTax = this.order.items.filter(item => {
            return !!item.exempt_from_tax && item.hash !== orderItem.hash;
        });
        if (otherItemsWithExemptFromTax.length) {
            const previouslySelectedReasons = otherItemsWithExemptFromTax.map(item => {
                return reasons.find(reason => reason.code === item.exempt_from_tax);
            }).filter(r => !!r);
            reasons = reasons.filter(reason => {
                const categoryWasPreviouslySelected = previouslySelectedReasons.some(r => r.category === reason.category);
                if (!categoryWasPreviouslySelected)
                    return true;
                return previouslySelectedReasons.some(r => r.code === reason.code);
            });
        }
        return reasons;
    }
    getFakeSdcResponse(order) {
        const tenant = Store.get('tenant');
        const prevResp = [...Order.all()].reverse().find(o => o.register_response)?.register_response;
        const registerResponse = {
            mrc: prevResp?.mrc || '00-1000-ABCDEFGH',
            tin: prevResp?.tin || tenant?.venue?.tax_id_number || '',
            address: prevResp?.address || tenant?.venue?.company_address || '',
            district: prevResp?.district || tenant?.venue?.city || '',
            messages: 'Success',
            signedBy: prevResp?.signedBy || 'ABCDEFGH',
            taxItems: order.taxes.map(t => {
                return {
                    rate: parseFloat(t.rate),
                    label: t.label,
                    amount: t.getValue().abs().toRoundedUnit(4),
                    categoryName: 'VAT',
                    categoryType: 0
                };
            }),
            requestedBy: prevResp?.requestedBy || 'ABCDEFGH',
            sdcDateTime: moment().format('YYYY-MM-DDTHH:mm:ss.SSSSSSSZZ'),
            totalAmount: order.getToPay().abs().toRoundedUnit(2),
            businessName: prevResp?.businessName || tenant?.venue?.name || '',
            locationName: prevResp?.locationName || tenant?.venue?.address || '',
            totalCounter: prevResp?.totalCounter || 12345,
            invoiceNumber: prevResp?.invoiceNumber || 'ABCDEFGH-ABCDEFGH-12345',
            invoiceCounter: prevResp?.invoiceCounter || '1234/12345ПП',
            verificationUrl: 'https://purs.gov.rs',
            taxGroupRevision: prevResp?.taxGroupRevision || 8,
            transactionTypeCounter: prevResp?.transactionTypeCounter || 12345,
            invoiceCounterExtension: FiscalTransactionExtensionType.NormalSale,
        };
        if (this.order.refersToAdvance()) {
            const advanceRefOrder = this.order.referredAdvanceOrders()[0];
            registerResponse.advancePaid = this.order.getPaidAdvance().toRoundedUnit(4);
            registerResponse.advanceTax = advanceRefOrder.getTax().abs().toRoundedUnit(4);
        }
        const copiesOrder = this.order.copiesOrder();
        if (copiesOrder?.getPaidAdvance().toUnit()) {
            registerResponse.advancePaid = copiesOrder.register_response?.advancePaid;
            registerResponse.advanceTax = copiesOrder.register_response?.advanceTax;
        }
        const correctsOrder = this.order.correctsOrders()[0];
        if (correctsOrder?.getPaidAdvance().toUnit()) {
            registerResponse.advancePaid = correctsOrder.register_response?.advancePaid;
            registerResponse.advanceTax = correctsOrder.register_response?.advanceTax;
        }
        return registerResponse;
    }
}
const OrderLocalizationSrStatic = OrderLocalizationSr;
//# sourceMappingURL=OrderLocalizationSr.js.map